<script setup>
import CardOperationsSeeContent from '@/StoreWeb/components/miscellaneous/conduent-support-reader/card-operations/CardOperationsSeeContent'
import CardOperationsConnectionLost from '@/StoreWeb/components/miscellaneous/conduent-support-reader/card-operations/errors/CardOperationsConnectionLost'
import CardOperationsOperationFailure from '@/StoreWeb/components/miscellaneous/conduent-support-reader/card-operations/errors/CardOperationsOperationFailure'
import { useLogin } from '@/StoreWeb/js/composables/login-utils'
import { useSession } from '@/StoreWeb/js/composables/session-utils'
import * as anonymousActionTypes from '@/StoreWeb/store/modules/anonymous/action-types'
import * as anonymousMutationTypes from '@/StoreWeb/store/modules/anonymous/mutation-types'
import * as cardReaderActionTypes from '@/StoreWeb/store/modules/card-reader/action-types'
import config from 'config'
import { isEmpty } from 'global-utils'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

const emit = defineEmits(['descriptionUpdated', 'componentUpdated', 'operationFinished', 'propsUpdated', 'stateUpdated'])

const props = defineProps({
    operationType: {
        type: String,
        default: 'READ',
        validator: (val) => ['READ', 'WRITE'].includes(val)
    },
    supportId: {
        type: String,
        default: ''
    }
})

const store = useStore()
const { t } = useI18n()
const { isAuthenticated } = useLogin()
const { changeFareMedia } = useSession()

const currentOperation = ref(null)
const currentOperationType = ref('READ')

const mediaSerialNumber = ref(null)
const mediaUnicity = ref(null)

const fareMedias = computed(() => store.getters.getWalletFareMedias)
const debugInformationsDisplay = computed(() => store.state.debuggingModule.debugInformationsDisplay)
const pluggedSupportId = computed(() => store.state.anonymousModule.pluggedSupportId)

function cleanStep () {
    emit('stateUpdated', '')
    emit('descriptionUpdated', '')
    emit('propsUpdated', null)
    emit('componentUpdated', null)
}

/* ------------------------------------------------------------- */
/*                         CARD OPERATIONS                       */
/* ------------------------------------------------------------- */
function activateChannel ({ operation, operationId, operationType, url, port }) {
    cleanStep()
    currentOperation.value = operation
    currentOperationType.value = operationType
    emit('stateUpdated', 'loading')
    emit('descriptionUpdated', t('card_reader:update_card:activate_channel_in_progress:message'))
    window.ActivateChannel(operationId, url, port)
}

function onActivateChannelHandler (event) {
    if (debugInformationsDisplay.value > 1) {
        // eslint-disable-next-line
        console.log(event.type, event.detail)
    }

    if (event.detail.response === -1) {
        emit('stateUpdated', 'error')
        emit('componentUpdated', CardOperationsConnectionLost)
    }
}

async function onEndTransferHandler (event) {
    cleanStep()

    if (debugInformationsDisplay.value > 1) {
        // eslint-disable-next-line
        console.log(event.type, event.detail)
    }

    emit('descriptionUpdated', t('card_reader:update_card:end_transfer:message'))

    if (event.detail.response === 0) {
        if (!isEmpty(currentOperation.value)) {
            if (debugInformationsDisplay.value > 1) {
                // eslint-disable-next-line
                console.log(`>>>> operationType : ${props.operationType} / currentOperationType : ${currentOperationType.value}`)
            }

            await getOperationReport()
        } else {
            if (debugInformationsDisplay.value > 1) {
                // eslint-disable-next-line
                console.log('No operation found...')
            }
        }
    } else {
        emit('stateUpdated', 'error')
        emit('propsUpdated', {
            description: event.detail.message,
            title: t('card_reader:operation:error:message')
        })
        emit('componentUpdated', CardOperationsOperationFailure)
    }
}

async function getOperationWrite (readOperationReportResponse) {
    mediaSerialNumber.value = readOperationReportResponse?.data.serialNumber
    mediaUnicity.value = readOperationReportResponse?.data.unicityId
    store.dispatch(cardReaderActionTypes.GET_OPERATION, {
        mediaSerialNumber: mediaSerialNumber.value,
        mediaUnicity: mediaUnicity.value,
        operationType: props.operationType,
        sessionId: currentOperation.value.sessionId,
        silent: true
    }).then((operationResponse) => {
        if (debugInformationsDisplay.value > 1) {
            // eslint-disable-next-line
            console.log('onEndTransferHandler >>> GET_OPERATION WRITE callback', operationResponse)
        }
        activateChannel({
            operation: operationResponse?.data,
            operationId: operationResponse?.data?.operationId,
            operationType: 'WRITE',
            url: operationResponse?.data?.serverHostname,
            port: operationResponse?.data?.serverPort
        })
    }).catch((error) => {
        setErrorMessageOnOperationWrite(error)
    })
}

function updateCard () {
    cleanStep()
    emit('stateUpdated', 'loading')
    emit('descriptionUpdated', t('card_reader:update_card:read_in_progress:message'))
}

async function getOperationReport () {
    const operationReportResponse = await store.dispatch(cardReaderActionTypes.GET_OPERATION_REPORT, {
        operation: currentOperation.value,
        operationType: currentOperationType.value,
        silent: true
    }).catch((error) => {
        if (currentOperationType.value === 'WRITE') {
            setErrorMessageOnOperationWrite(error)
        } else {
            setErrorMessageOnOperationRead(error)
        }
    })

    if (operationReportResponse) {
        if (props.operationType === 'READ') {
            emit('operationFinished', {
                support: {
                    providerId: 'conduent',
                    isTemporary: false,
                    fareMediaId: operationReportResponse?.data?.serialNumber
                }
            })
        } else {
            if (currentOperationType.value === 'READ') {
                if (debugInformationsDisplay.value > 1) {
                    // eslint-disable-next-line
                    console.log('onEndTransferHandler >>> GET_OPERATION_REPORT READ callback', operationReportResponse)
                }

                updateCard()
                await getOperationWrite(operationReportResponse)
            } else {
                if (debugInformationsDisplay.value > 1) {
                    // eslint-disable-next-line
                    console.log('onEndTransferHandler >>> GET_OPERATION_REPORT ' + currentOperationType.value + ' callback ', operationReportResponse)
                }

                if (operationReportResponse?.data?.mediaStatusResult === 'OK') {
                    emit('stateUpdated', 'completed')
                    emit('componentUpdated', CardOperationsSeeContent)
                }
            }
        }
    }
}

async function seeCardContent () {
    const support = {
        fareMediaId: pluggedSupportId.value,
        isTemporary: false,
        providerId: 'conduent'
    }

    if (isAuthenticated.value && fareMedias.value.length > 0) {
        const fareMedia = fareMedias.value.find(walletFareMedia => walletFareMedia.id === pluggedSupportId.value)

        if (!isEmpty(fareMedia)) {
            changeFareMedia(fareMedia)
        }
    } else {
        const content = await store.dispatch(anonymousActionTypes.GET_SUPPORT_CONTENT, support)

        if (content?.id) {
            store.commit(anonymousMutationTypes.SET_SUPPORT_CONTENT, content)
        }
    }

    emit('operationFinished', {
        goToWallet: true,
        support
    })
}

function setErrorMessageOnOperationRead (error) {
    const { status, message } = error.response?.data || {}
    const description = (status === 422 || status === 404) && message ? message : t('card_reader:load:error_message')

    emit('stateUpdated', 'error')
    emit('propsUpdated', {
        description,
        title: t('card_reader:operation:error:message')
    })
    emit('componentUpdated', CardOperationsOperationFailure)
}

function setErrorMessageOnOperationWrite (error) {
    const { status, message } = error.response?.data || {}
    const description = (status === 422 || status === 404) && message ? message : t('card_reader:update_card:load:error_message')

    emit('stateUpdated', 'error')
    emit('propsUpdated', {
        description,
        title: t('card_reader:operation:error:message')
    })
    emit('componentUpdated', CardOperationsOperationFailure)
}

/* ------------------------------------------------------------- */
/*                     MOUNTED / UNMOUNTED                       */
/* ------------------------------------------------------------- */
onMounted(() => {
    window.conduentMobilitySolutionsUrl = config.support_reader.conduent_mobility_solutions_url
    document.body.addEventListener('card-reader-activate-channel', onActivateChannelHandler)
    document.body.addEventListener('card-reader-end-transfer', onEndTransferHandler)
})

onUnmounted(() => {
    document.body.removeEventListener('card-reader-activate-channel', onActivateChannelHandler)
    document.body.removeEventListener('card-reader-end-transfer', onEndTransferHandler)
})

/* ------------------------------------------------------------- */
/*                        EXPOSED METHODS                        */
/* ------------------------------------------------------------- */
defineExpose({ activateChannel, seeCardContent })
</script>
